import { useState } from "react";
import { Link } from "react-router-dom";

const platformIcon: Record<string, string> = {
  tiktok: "ri-tiktok-line",
  youtube: "ri-youtube-line",
  twitter: "ri-twitter-x-line",
  instagram: "ri-instagram-line",
  facebook: "ri-facebook-circle-line",
};

const platformColor: Record<string, string> = {
  tiktok: "text-gray-900",
  youtube: "text-red-500",
  twitter: "text-gray-700",
  instagram: "text-pink-500",
  facebook: "text-amber-800",
};

interface Post {
  id: string;
  user: string;
  username: string;
  avatar: string;
  role: string;
  platform: string;
  followers: string | null;
  content: string;
  image: string;
  likes: number;
  comments: number;
  shares: number;
  time: string;
  tagged: string[];
  type: string;
  earned: string | null;
  tag: string | null;
}

interface PostCardProps {
  post: Post;
  onShareProduct?: () => void;
}

export default function PostCard({ post, onShareProduct }: PostCardProps) {
  const [liked, setLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState<string[]>([]);

  const handleComment = () => {
    if (commentText.trim()) {
      setComments((prev) => [commentText.trim(), ...prev]);
      setCommentText("");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4 hover:border-orange-100 transition-colors">
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border-2 border-orange-100">
            <img src={post.avatar} alt={post.user} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-bold text-sm text-gray-900">{post.user}</span>
              {post.role === "influencer" && (
                <i className="ri-verified-badge-fill text-orange-500 text-xs" />
              )}
              {post.platform && platformIcon[post.platform] && (
                <span className={`text-xs ${platformColor[post.platform]}`}>
                  <i className={platformIcon[post.platform]} />
                </span>
              )}
              {post.followers && (
                <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full font-semibold">
                  {post.followers}
                </span>
              )}
              {post.type === "product" && post.tag && (
                <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold border border-amber-200">
                  <i className="ri-price-tag-3-line mr-0.5" />{post.tag}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{post.username} · {post.time}</div>
          </div>
          {post.earned && (
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-gray-400">Earned</div>
              <div className="text-sm font-extrabold text-green-600">{post.earned}</div>
            </div>
          )}
          <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-50 flex-shrink-0">
            <i className="ri-more-line" />
          </button>
        </div>

        <p className="text-sm text-gray-800 leading-relaxed mt-3">{post.content}</p>
      </div>

      {/* Image */}
      {post.image && (
        <div className="w-full h-72 overflow-hidden">
          <img
            src={post.image}
            alt={post.user}
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 flex items-center gap-1 border-t border-gray-50">
        <button
          onClick={() => setLiked((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer ${liked ? "text-red-500 bg-red-50" : "text-gray-500 hover:text-red-400 hover:bg-red-50"}`}
        >
          <i className={liked ? "ri-heart-fill" : "ri-heart-line"} />
          <span>{(post.likes + (liked ? 1 : 0)).toLocaleString()}</span>
        </button>

        <button
          onClick={() => setShowComments((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-orange-500 hover:bg-orange-50 transition-colors cursor-pointer"
        >
          <i className="ri-chat-1-line" />
          <span>{post.comments + comments.length}</span>
        </button>

        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-green-500 hover:bg-green-50 transition-colors cursor-pointer">
          <i className="ri-share-forward-line" />
          <span>{post.shares.toLocaleString()}</span>
        </button>

        {post.type === "product" && post.tagged.length > 0 && (
          <Link
            to={`/product/${post.tagged[0]}`}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-bag-line" /> Shop & Earn
          </Link>
        )}

        {post.type === "life" && onShareProduct && (
          <button
            onClick={onShareProduct}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-money-dollar-circle-line" /> Share & Earn
          </button>
        )}
      </div>

      {/* Comments */}
      {showComments && (
        <div className="px-4 pb-4 border-t border-gray-50">
          <div className="flex gap-2 mt-3">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-400 flex-shrink-0">
              <i className="ri-user-line text-sm" />
            </div>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleComment()}
                placeholder="Write a comment..."
                className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-orange-400 transition-colors"
              />
              <button
                onClick={handleComment}
                className="px-4 py-2 bg-orange-500 text-white text-xs font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Post
              </button>
            </div>
          </div>
          {comments.length > 0 && (
            <div className="mt-3 space-y-2">
              {comments.map((c, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-400 flex-shrink-0">
                    <i className="ri-user-line text-xs" />
                  </div>
                  <div className="bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-700 flex-1">{c}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
